import React, { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Search, Camera, Trash2, Globe, FilterX, ShieldAlert, Download, Printer, ClipboardList, X } from "lucide-react";
import { toast } from "sonner";
import BarcodeScanner from "../components/BarcodeScanner";
import { useAuth } from "../lib/auth";
import { canDo } from "../lib/roles";
import { PRODUCT_CATEGORIES, PRODUCT_CATEGORY_SET } from "../constants/products";
import { safeSupplierHref } from "./Lieferanten";
import {
  assembleCsv, downloadCsv, germanNumber, isoDate, slugForFilename,
} from "../lib/csv";
import { openPrintReport } from "../lib/print";

const EMPTY = {
  internal_id: "", ean: "", name: "", brand: "", category: "",
  purchase_price: 0, sales_price: 0, vat_rate: 19,
  supplier_id: "", supplier_article_no: "", package_unit: "",
  min_stock: 0, active: true, age_restricted: false,
};

const ALL_VAL = "__all__";
const NONE_VAL = "__none__";

export default function Produkte() {
  const { user } = useAuth();
  const canEdit = canDo(user, "product.edit");
  const [params, setParams] = useSearchParams();

  const [q, setQ] = useState(() => params.get("q") || "");
  // Lieferantenfilter — Startwert aus URL-Parameter `?supplier=`
  // (Deep-Link aus Lieferantenseite „Produkte anzeigen").
  const [supplierFilter, setSupplierFilter] = useState(() => params.get("supplier") || ALL_VAL);
  const [categoryFilter, setCategoryFilter] = useState(() => params.get("category") || ALL_VAL);
  const [vatFilter, setVatFilter] = useState(() => params.get("vat") || ALL_VAL);
  const [ageFilter, setAgeFilter] = useState(() => params.get("age") || ALL_VAL);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [scannerOpen, setScannerOpen] = useState(false);
  // V1b — Mehrfachauswahl über Filterwechsel hinweg. IDs statt Objekte,
  // damit die Auswahl deterministisch bleibt und nicht durch neue
  // Query-Ergebnisse überschrieben wird.
  const [selected, setSelected] = useState(() => new Set());
  // Operations Polish 2026-08-30: Bugfix für „Liste aus Auswahl".
  // Vorher wurde die Arbeitsliste aus `allProducts` (aktuelle,
  // gefilterte Query-Ergebnisse) gebaut → durch Filterwechsel
  // verschwundene Produkte fehlten in der Liste, obwohl die Selection
  // sie noch enthielt. Fix: parallel zur ID-Set-Selection halten wir
  // eine ID→Produkt-Map, die alle je gesehenen ausgewählten Produkte
  // aufbewahrt. Sie ist die Source of Truth für die Listen-Erstellung
  // (nicht die aktuell gefilterte Query).
  const [selectedProducts, setSelectedProducts] = useState(() => ({}));
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const qc = useQueryClient();

  // Filter ↔ URL synchronisieren, damit Deep-Links (auch nach Reload)
  // die Filterung wiederherstellen und das Zurück-Sharen von URLs
  // dieselbe Ansicht liefert.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    if (supplierFilter !== ALL_VAL) next.set("supplier", supplierFilter); else next.delete("supplier");
    if (categoryFilter !== ALL_VAL) next.set("category", categoryFilter); else next.delete("category");
    if (vatFilter !== ALL_VAL) next.set("vat", vatFilter); else next.delete("vat");
    if (ageFilter !== ALL_VAL) next.set("age", ageFilter); else next.delete("age");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, supplierFilter, categoryFilter, vatFilter, ageFilter]);

  // Backend-seitige Filter → deterministische Ergebnisliste.
  const queryParams = useMemo(() => {
    const p = {};
    if (q) p.q = q;
    if (supplierFilter !== ALL_VAL) p.supplier_id = supplierFilter;
    if (categoryFilter !== ALL_VAL) p.category = categoryFilter;
    if (vatFilter !== ALL_VAL) p.vat_rate = Number(vatFilter);
    if (ageFilter !== ALL_VAL) p.age_restricted = ageFilter === "yes";
    return p;
  }, [q, supplierFilter, categoryFilter, vatFilter, ageFilter]);

  const products = useQuery({
    queryKey: ["products", queryParams],
    queryFn: async () => (await api.get("/products", { params: queryParams })).data,
  });
  const suppliers = useQuery({
    queryKey: ["suppliers"],
    queryFn: async () => (await api.get("/suppliers")).data,
  });

  const vatOptions = useMemo(() => {
    const s = new Set([7, 19]);
    (products.data || []).forEach((p) => {
      const v = Number(p.vat_rate);
      if (Number.isFinite(v)) s.add(v);
    });
    return Array.from(s).sort((a, b) => a - b);
  }, [products.data]);

  const upsert = useMutation({
    mutationFn: async () => {
      const body = { ...form,
        purchase_price: +form.purchase_price,
        sales_price: +form.sales_price,
        vat_rate: +form.vat_rate,
        min_stock: +form.min_stock,
        supplier_id: form.supplier_id || null,
        category: form.category || null,
        age_restricted: !!form.age_restricted,
      };
      if (editing) return (await api.put(`/products/${editing.id}`, body)).data;
      return (await api.post("/products", body)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Produkt aktualisiert" : "Produkt angelegt");
      setOpen(false); setEditing(null); setForm(EMPTY);
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["kpis"] });
      qc.invalidateQueries({ queryKey: ["inv-by-product"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p,
      supplier_id: p.supplier_id || "",
      category: p.category || "",
      age_restricted: !!p.age_restricted,
    });
    setOpen(true);
  };

  async function archiveProduct(p) {
    const msg = `„${p.name}" entfernen?\n\nFalls Buchungen/POS-Belege existieren wird das Produkt archiviert (Historie bleibt). Sonst endgültig gelöscht.`;
    if (!window.confirm(msg)) return;
    try {
      try {
        await api.delete(`/products/${p.id}/hard`);
        toast.success("Produkt endgültig gelöscht");
      } catch (err) {
        if (err?.response?.status === 409) {
          await api.delete(`/products/${p.id}`);
          toast.success("Produkt archiviert (Historie bleibt erhalten)");
        } else { throw err; }
      }
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["kpis"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen");
    }
  }

  const supplierMap = useMemo(
    () => Object.fromEntries((suppliers.data || []).map((s) => [s.id, s])),
    [suppliers.data]
  );

  const anyFilterActive =
    supplierFilter !== ALL_VAL || categoryFilter !== ALL_VAL ||
    vatFilter !== ALL_VAL || ageFilter !== ALL_VAL || !!q;

  const resetFilters = () => {
    setQ("");
    setSupplierFilter(ALL_VAL);
    setCategoryFilter(ALL_VAL);
    setVatFilter(ALL_VAL);
    setAgeFilter(ALL_VAL);
  };

  const supplierLabel = useMemo(() => {
    if (supplierFilter === ALL_VAL) return "Alle";
    return supplierMap[supplierFilter]?.name || supplierFilter;
  }, [supplierFilter, supplierMap]);

  // V1b — Selection-Helpers.
  const toggleOne = (id, product) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
    // Operations Polish 2026-08-30: Produkt-Snapshot merken, damit die
    // Auswahl auch dann vollständig in die Arbeitsliste wandert, wenn
    // durch Filterwechsel das Produkt gerade nicht mehr sichtbar ist.
    if (product) {
      setSelectedProducts((prev) => ({ ...prev, [id]: product }));
    }
  };
  const clearSelection = () => { setSelected(new Set()); setSelectedProducts({}); };
  // Stabile Referenz — ansonsten meldet ESLint zu Recht instabile deps für
  // useMemo(visibleIds); Verhalten bleibt identisch (nur Referenz-Stabilität).
  const visibleRows = useMemo(() => products.data || [], [products.data]);
  const visibleIds = useMemo(() => visibleRows.map((p) => p.id), [visibleRows]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someVisibleSelected = !allVisibleSelected && visibleIds.some((id) => selected.has(id));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => n.delete(id));
      } else {
        visibleIds.forEach((id) => n.add(id));
      }
      return n;
    });
    // Beim Bulk-Anhaken die aktuell sichtbaren Produkte im Snapshot
    // ergänzen; beim Bulk-Abhaken bewusst NICHTS entfernen (die
    // Snapshots gehen ohnehin nur an das Listen-Modal, das seinerseits
    // strikt die `selected`-Menge nutzt).
    if (!allVisibleSelected) {
      setSelectedProducts((prev) => {
        const next = { ...prev };
        visibleRows.forEach((p) => { next[p.id] = p; });
        return next;
      });
    }
  };

  const exportCsv = () => {
    const rows = products.data || [];
    if (!rows.length) {
      toast.error("Keine Produkte im aktuellen Filter — nichts zu exportieren.");
      return;
    }
    const headers = ["Produkt", "SKU", "EAN", "Marke", "Kategorie", "Lieferant", "MwSt (%)", "EK", "VK", "Ab 18", "Mindestbestand"];
    const dataRows = rows.map((p) => [
      p.name,
      p.internal_id || "",
      p.ean || "",
      p.brand || "",
      p.category || "",
      supplierMap[p.supplier_id]?.name || "",
      Number(p.vat_rate || 0),
      germanNumber(p.purchase_price),
      germanNumber(p.sales_price),
      p.age_restricted ? "Ja" : "Nein",
      p.min_stock,
    ]);
    // CSV = reine Datentabelle (kein Metadatenblock). Filter definieren
    // ausschließlich die Ergebnismenge; menschenlesbare Filter-
    // Zusammenfassung wandert in den Drucken-Export.
    const csv = assembleCsv({ headers, rows: dataRows });
    const suffix = supplierFilter !== ALL_VAL ? `-${slugForFilename(supplierLabel)}` : "";
    downloadCsv(`loop-produkte${suffix}-${isoDate()}.csv`, csv);
    toast.success(`${rows.length} Produkte exportiert`);
  };

  const printReport = () => {
    const rows = products.data || [];
    if (!rows.length) {
      toast.error("Keine Produkte im aktuellen Filter — nichts zu drucken.");
      return;
    }
    const filters = [
      { label: "Lieferant", value: supplierLabel },
      { label: "Kategorie", value: categoryFilter === ALL_VAL ? "Alle" : categoryFilter },
      { label: "MwSt.", value: vatFilter === ALL_VAL ? "Alle" : `${vatFilter} %` },
      { label: "Ab 18", value: ageFilter === ALL_VAL ? "Alle" : (ageFilter === "yes" ? "Ja" : "Nein") },
      { label: "Suche", value: q ? q : "—" },
    ];
    const columns = [
      { label: "Produkt" },
      { label: "SKU" },
      { label: "EAN" },
      { label: "Marke" },
      { label: "Kategorie" },
      { label: "Lieferant" },
      { label: "MwSt.", align: "right" },
      { label: "EK", align: "right" },
      { label: "VK", align: "right" },
      { label: "Ab 18", align: "center" },
      { label: "Min.", align: "right" },
    ];
    const dataRows = rows.map((p) => [
      p.name,
      p.internal_id || "",
      p.ean || "",
      p.brand || "",
      p.category || "",
      supplierMap[p.supplier_id]?.name || "",
      `${Number(p.vat_rate || 0)} %`,
      germanNumber(p.purchase_price),
      germanNumber(p.sales_price),
      p.age_restricted ? "Ja" : "—",
      String(p.min_stock),
    ]);
    const result = openPrintReport({
      area: "Produkte", filters, columns, rows: dataRows, resultCount: rows.length,
    });
    if (!result.ok) {
      toast.error("Popup-Blocker verhindert die Druckvorschau. Bitte in den Browsereinstellungen erlauben.");
    }
  };

  return (
    <div data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-cyan-400">Stammdaten</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight truncate">Produkte</h1>
          {supplierFilter !== ALL_VAL && (
            <div className="mt-2 text-xs uppercase tracking-widest text-cyan-400/80" data-testid="products-supplier-banner">
              Gefiltert nach Lieferant: <span className="text-cyan-300">{supplierLabel}</span>
            </div>
          )}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          {canEdit && (
            <DialogTrigger asChild>
              <Button
                data-testid="new-product-btn"
                onClick={openNew}
                className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-sm uppercase tracking-wider font-semibold"
              >
                <Plus size={16} className="mr-2" /> Neues Produkt
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="bg-zinc-950 border-zinc-800 sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display uppercase tracking-wide">
                {editing ? "Produkt bearbeiten" : "Neues Produkt"}
              </DialogTitle>
              <DialogDescription className="text-xs text-zinc-500">
                Stammdaten inkl. EAN, Preisen und Lieferant pflegen.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["name", "Name *", "text"],
                ["ean", "EAN / GTIN", "text"],
                ["internal_id", "Interne ID (SKU)", "text"],
                ["brand", "Marke", "text"],
                ["package_unit", "Verpackung", "text"],
                ["purchase_price", "Einkaufspreis (€)", "number"],
                ["sales_price", "Verkaufspreis (€)", "number"],
                ["vat_rate", "MwSt %", "number"],
                ["min_stock", "Mindestbestand", "number"],
                ["supplier_article_no", "Lieferanten-Art.-Nr.", "text"],
              ].map(([k, label, type]) => (
                <div key={k} className={k === "name" ? "sm:col-span-2" : ""}>
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
                  <div className={k === "ean" ? "flex gap-2" : ""}>
                    <Input
                      data-testid={`product-field-${k}`}
                      type={type}
                      step={type === "number" ? "0.01" : undefined}
                      className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm w-full"
                      value={form[k] ?? ""}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    />
                    {k === "ean" && (
                      <Button
                        type="button"
                        data-testid="product-ean-scan-btn"
                        onClick={() => setScannerOpen(true)}
                        className="mt-1 h-11 shrink-0 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-cyan-400 px-3"
                      >
                        <Camera size={16} className="mr-1" />
                        <span className="text-xs uppercase tracking-wider">Kamera</span>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <div className="sm:col-span-1">
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Kategorie</Label>
                <Select
                  value={form.category || NONE_VAL}
                  onValueChange={(v) => setForm({ ...form, category: v === NONE_VAL ? "" : v })}
                >
                  <SelectTrigger data-testid="product-field-category"
                    className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm">
                    <SelectValue placeholder="Kategorie wählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    <SelectItem value={NONE_VAL}>— Keine —</SelectItem>
                    {PRODUCT_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                    {form.category && !PRODUCT_CATEGORY_SET.has(form.category) && (
                      <SelectItem value={form.category}>
                        {form.category} (bestehend)
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-1 flex items-end pb-1">
                <label
                  className="inline-flex items-center gap-2 cursor-pointer select-none"
                  data-testid="product-field-age-restricted-label"
                >
                  <Checkbox
                    data-testid="product-field-age-restricted"
                    checked={!!form.age_restricted}
                    onCheckedChange={(v) => setForm({ ...form, age_restricted: !!v })}
                  />
                  <span className="text-sm uppercase tracking-wider text-zinc-200">
                    <ShieldAlert size={14} className="inline mr-1 text-rose-400" /> Ab 18 Jahren
                  </span>
                </label>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Lieferant</Label>
                <Select
                  value={form.supplier_id || "none"}
                  onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="product-supplier-select" className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm">
                    <SelectValue placeholder="Kein Lieferant" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    <SelectItem value="none">— Kein Lieferant —</SelectItem>
                    {(suppliers.data || []).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button
                data-testid="product-save-btn"
                onClick={() => upsert.mutate()}
                disabled={!form.name || upsert.isPending}
                className="bg-cyan-400 text-zinc-950 hover:bg-cyan-300 rounded-sm"
              >
                {upsert.isPending ? "Speichere…" : "Speichern"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={(code) => {
          setForm((f) => ({ ...f, ean: code }));
          return { ok: true, name: "EAN übernommen", ean: code };
        }}
      />

      {/* Filterleiste — Suche + 4 Mehrfachfilter */}
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
        <div className="relative sm:col-span-2">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="products-search-input"
            placeholder="Suche: Name, EAN, SKU, Lieferant…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-11 bg-zinc-900 border-zinc-800 rounded-sm"
          />
        </div>
        <Select value={supplierFilter} onValueChange={setSupplierFilter}>
          <SelectTrigger data-testid="products-filter-supplier"
            className="h-11 bg-zinc-900 border-zinc-800 rounded-sm">
            <SelectValue placeholder="Lieferant" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value={ALL_VAL}>Alle Lieferanten</SelectItem>
            {(suppliers.data || []).map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger data-testid="products-filter-category"
            className="h-11 bg-zinc-900 border-zinc-800 rounded-sm">
            <SelectValue placeholder="Kategorie" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value={ALL_VAL}>Alle Kategorien</SelectItem>
            {PRODUCT_CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={vatFilter} onValueChange={setVatFilter}>
          <SelectTrigger data-testid="products-filter-vat"
            className="h-11 bg-zinc-900 border-zinc-800 rounded-sm">
            <SelectValue placeholder="MwSt." />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value={ALL_VAL}>Alle MwSt.</SelectItem>
            {vatOptions.map((v) => (
              <SelectItem key={v} value={String(v)}>{v} %</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={ageFilter} onValueChange={setAgeFilter}>
          <SelectTrigger data-testid="products-filter-age"
            className="h-11 bg-zinc-900 border-zinc-800 rounded-sm">
            <SelectValue placeholder="Ab 18" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value={ALL_VAL}>Alle Altersfreigaben</SelectItem>
            <SelectItem value="yes">Ab 18</SelectItem>
            <SelectItem value="no">Nicht altersbeschränkt</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button
          data-testid="products-csv-export"
          onClick={exportCsv}
          variant="outline"
          size="sm"
          className="h-9 border-zinc-700 hover:bg-zinc-800 rounded-sm text-cyan-400 hover:text-cyan-300 uppercase tracking-widest text-[11px]"
        >
          <Download size={14} className="mr-2" /> CSV exportieren
        </Button>
        <Button
          data-testid="products-print"
          onClick={printReport}
          variant="outline"
          size="sm"
          className="h-9 border-zinc-700 hover:bg-zinc-800 rounded-sm text-zinc-200 hover:text-white uppercase tracking-widest text-[11px]"
        >
          <Printer size={14} className="mr-2" /> Drucken
        </Button>
        {/* V1b — Bulk-Action: Auswahl → Arbeitsliste erstellen. */}
        <Button
          data-testid="products-create-list-btn"
          onClick={() => setListDialogOpen(true)}
          disabled={selected.size === 0}
          size="sm"
          className="h-9 rounded-sm bg-cyan-500 hover:bg-cyan-400 text-zinc-950 uppercase tracking-widest font-semibold text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ClipboardList size={14} className="mr-2" />
          Liste aus Auswahl · {selected.size}
        </Button>
        {selected.size > 0 && (
          <Button
            data-testid="products-clear-selection"
            onClick={clearSelection}
            variant="ghost"
            size="sm"
            className="h-9 text-zinc-400 hover:text-zinc-100 uppercase tracking-widest text-[11px]"
          >
            <X size={14} className="mr-1" /> Auswahl leeren
          </Button>
        )}
        {anyFilterActive && (
          <Button
            data-testid="products-filter-reset"
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            className="h-9 text-cyan-400 hover:text-cyan-300 uppercase tracking-widest text-[11px]"
          >
            <FilterX size={14} className="mr-1" /> Filter zurücksetzen
          </Button>
        )}
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 ml-auto"
          data-testid="products-result-count">
          {(products.data || []).length} Produkte
        </span>
      </div>

      <div className="border border-zinc-800 bg-zinc-900 rounded-sm">
        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-zinc-800" data-testid="products-mobile-list">
          {(products.data || []).map((p) => {
            const sup = supplierMap[p.supplier_id];
            const supHref = safeSupplierHref(sup?.website);
            const isChecked = selected.has(p.id);
            return (
            <div key={p.id} className="p-4 flex items-start gap-3">
              <Checkbox
                data-testid={`product-select-m-${p.id}`}
                checked={isChecked}
                onCheckedChange={() => toggleOne(p.id, p)}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium break-anywhere">
                  {p.name}
                  {p.age_restricted && (
                    <span data-testid={`product-age-badge-m-${p.id}`}
                      className="ml-2 inline-block px-1.5 py-0.5 rounded-sm bg-rose-500/20 text-rose-300 text-[10px] uppercase font-mono tracking-widest align-middle">
                      Ab 18
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 font-mono mt-0.5 break-anywhere">
                  {p.internal_id && <div>SKU: {p.internal_id}</div>}
                  {p.ean && <div>EAN: {p.ean}</div>}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-zinc-400">
                  {p.brand && <span>{p.brand}</span>}
                  {p.category && <span className="text-zinc-500">· {p.category}</span>}
                  {sup && (
                    <span className="text-zinc-500">
                      · {supHref ? (
                        <a href={supHref} target="_blank" rel="noopener noreferrer"
                          data-testid={`product-supplier-link-m-${p.id}`}
                          className="text-cyan-400 hover:text-cyan-300 underline-offset-4 hover:underline inline-flex items-center gap-1">
                          <Globe size={10} /> {sup.name}
                        </a>
                      ) : sup.name}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs font-mono tabular-nums">
                  <span className="text-zinc-500">EK <span className="text-zinc-200">{Number(p.purchase_price || 0).toFixed(2)}</span></span>
                  <span className="text-zinc-500">VK <span className="text-zinc-200">{Number(p.sales_price || 0).toFixed(2)}</span></span>
                  <span className="text-zinc-500">MwSt <span className="text-zinc-200" data-testid={`product-vat-m-${p.id}`}>{Number(p.vat_rate || 0)} %</span></span>
                  <span className="text-zinc-500">Min <span className="text-zinc-200">{p.min_stock}</span></span>
                </div>
              </div>
              {canEdit && (
                <div className="flex flex-col shrink-0 gap-1">
                  <Button
                    data-testid={`product-edit-m-${p.id}`}
                    size="icon" variant="ghost"
                    className="text-zinc-400 hover:text-cyan-400"
                    onClick={() => openEdit(p)}
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    data-testid={`product-delete-m-${p.id}`}
                    size="icon" variant="ghost"
                    className="text-rose-400 hover:text-rose-300"
                    onClick={() => archiveProduct(p)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              )}
            </div>
          );})}
          {!products.data?.length && (
            <div className="p-8 text-center text-zinc-500 text-sm">Keine Produkte gefunden</div>
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="px-3 py-3 w-8">
                  <Checkbox
                    data-testid="products-select-all"
                    checked={allVisibleSelected}
                    onCheckedChange={toggleAllVisible}
                    aria-label="Alle sichtbaren auswählen"
                    className={someVisibleSelected ? "opacity-70" : ""}
                  />
                </th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3 font-mono">EAN</th>
                <th className="text-left px-4 py-3">Kategorie</th>
                <th className="text-left px-4 py-3">Lieferant</th>
                <th className="text-right px-4 py-3 font-mono">EK</th>
                <th className="text-right px-4 py-3 font-mono">VK</th>
                <th className="text-right px-4 py-3 font-mono">MwSt.</th>
                <th className="text-center px-4 py-3 font-mono">Ab 18</th>
                <th className="text-right px-4 py-3 font-mono">Min.</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800" data-testid="products-table-body">
              {(products.data || []).map((p) => {
                const sup = supplierMap[p.supplier_id];
                const supHref = safeSupplierHref(sup?.website);
                const isChecked = selected.has(p.id);
                return (
                <tr key={p.id} className={`hover:bg-zinc-800/60 ${isChecked ? "bg-cyan-500/5" : ""}`}>
                  <td className="px-3 py-3 text-center">
                    <Checkbox
                      data-testid={`product-select-${p.id}`}
                      checked={isChecked}
                      onCheckedChange={() => toggleOne(p.id, p)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.name}</div>
                    {p.brand && <div className="text-xs text-zinc-500">{p.brand}</div>}
                    {p.internal_id && <div className="text-xs text-zinc-500 font-mono">{p.internal_id}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-300">{p.ean || "—"}</td>
                  <td className="px-4 py-3 text-zinc-300">{p.category || "—"}</td>
                  <td className="px-4 py-3 text-zinc-300">
                    {sup ? (
                      supHref ? (
                        <a href={supHref} target="_blank" rel="noopener noreferrer"
                          data-testid={`product-supplier-link-${p.id}`}
                          className="text-cyan-400 hover:text-cyan-300 underline-offset-4 hover:underline inline-flex items-center gap-1">
                          <Globe size={12} /> {sup.name}
                        </a>
                      ) : (
                        <span data-testid={`product-supplier-text-${p.id}`}>{sup.name}</span>
                      )
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{Number(p.purchase_price || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{Number(p.sales_price || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums" data-testid={`product-vat-${p.id}`}>
                    {Number(p.vat_rate || 0)} %
                  </td>
                  <td className="px-4 py-3 text-center">
                    {p.age_restricted ? (
                      <span data-testid={`product-age-badge-${p.id}`}
                        className="inline-block px-2 py-0.5 rounded-sm bg-rose-500/20 text-rose-300 text-[11px] uppercase font-mono tracking-widest">
                        Ab 18
                      </span>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{p.min_stock}</td>
                  <td className="px-4 py-3 text-right">
                    {canEdit && (
                      <div className="inline-flex gap-1">
                        <Button
                          data-testid={`product-edit-${p.id}`}
                          size="sm" variant="ghost"
                          className="text-zinc-400 hover:text-cyan-400"
                          onClick={() => openEdit(p)}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          data-testid={`product-delete-${p.id}`}
                          size="sm" variant="ghost"
                          className="text-rose-400 hover:text-rose-300"
                          onClick={() => archiveProduct(p)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );})}
              {!products.data?.length && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-zinc-500">Keine Produkte gefunden</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateListFromSelection
        open={listDialogOpen}
        onOpenChange={setListDialogOpen}
        selectedIds={Array.from(selected)}
        allProducts={
          // Operations Polish 2026-08-30: Merge Snapshot-Map (Source of Truth
          // für die Auswahl) mit den aktuell sichtbaren Zeilen. Damit
          // landet garantiert JEDES ausgewählte Produkt in der Arbeitsliste,
          // unabhängig davon, welche Filter beim Öffnen des Dialogs aktiv sind.
          (() => {
            const map = { ...selectedProducts };
            (products.data || []).forEach((p) => { if (!map[p.id]) map[p.id] = p; });
            return Object.values(map);
          })()
        }
        onCreated={() => {
          setListDialogOpen(false);
          clearSelection();
          qc.invalidateQueries({ queryKey: ["work-lists"] });
        }}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* V1b — Modal: Arbeitsliste aus aktueller Produkt-Auswahl erzeugen */
/* ---------------------------------------------------------------- */

const WL_TYPES = [
  { value: "generic", label: "Allgemein" },
  { value: "purchase", label: "Einkauf" },
  { value: "transfer", label: "Umlagerung" },
  { value: "pack", label: "Packliste" },
];

function CreateListFromSelection({ open, onOpenChange, selectedIds, allProducts, onCreated }) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("purchase");
  const idSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const rows = useMemo(
    () => (allProducts || []).filter((p) => idSet.has(p.id)),
    [allProducts, idSet]
  );

  useEffect(() => {
    if (open) {
      // Titel-Vorschlag mit Datum + Anzahl. Wird bei Neu-Öffnen aktualisiert.
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      setTitle(`Auswahl vom ${stamp} · ${selectedIds.length} Produkt${selectedIds.length === 1 ? "" : "e"}`);
      setType("purchase");
    }
  }, [open, selectedIds.length]);

  const createMut = useMutation({
    mutationFn: async () => {
      const items = rows.map((p, idx) => ({
        product_id: p.id, quantity: 1, sort_order: idx,
      }));
      return (await api.post("/work-lists", {
        title: title.trim(), type, items,
      })).data;
    },
    onSuccess: (wl) => {
      toast.success(`Arbeitsliste „${wl.title}" angelegt`);
      onCreated();
    },
    onError: (e) =>
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="products-create-list-dialog" className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wide">
            Liste aus Auswahl erstellen
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            {rows.length} Produkt{rows.length === 1 ? "" : "e"} aus der aktuellen Auswahl.
            Arbeitslisten verändern keinen Bestand.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Titel</Label>
            <Input
              data-testid="products-create-list-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-zinc-900 border-zinc-800 rounded-sm mt-1"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Typ</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="products-create-list-type" className="bg-zinc-900 border-zinc-800 rounded-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {WL_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border-t border-zinc-800 pt-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
              Produkte in der Liste ({rows.length})
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1 pr-1" data-testid="products-create-list-preview">
              {rows.length === 0 && (
                <div className="text-xs text-zinc-600 italic">Keine Produkte ausgewählt.</div>
              )}
              {rows.map((p) => (
                <div key={p.id} className="text-xs text-zinc-200 flex justify-between gap-2 border border-zinc-800 rounded-sm px-2 py-1.5">
                  <span className="truncate">{p.name}</span>
                  {p.ean && <span className="text-zinc-500 font-mono shrink-0">{p.ean}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-3">
          <Button
            variant="outline"
            className="border-zinc-700 rounded-sm"
            onClick={() => onOpenChange(false)}
            data-testid="products-create-list-cancel"
          >
            Abbrechen
          </Button>
          <Button
            data-testid="products-create-list-save"
            disabled={!title.trim() || rows.length === 0 || createMut.isPending}
            onClick={() => createMut.mutate()}
            className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 uppercase tracking-widest font-bold rounded-sm"
          >
            {createMut.isPending ? "Erstelle…" : "Liste anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

